import { IconPlusCircle } from "@/assets/icons/geist/IconPlusCircle";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { Heading3, Heading4 } from "@/components/podkit/typography/Headings";
import { TrackLocations } from "@/hooks/use-segment";
import { useListSCIMConfigurations, type PlainSCIMConfiguration } from "@/queries/scim-queries";
import { SCIMConfigCard } from "@/routes/settings/login/SCIMConfigCard";
import { ConfigureSCIM } from "@/routes/settings/login/ConfigureSCIM";
import { RegenerateSCIMTokenModal } from "@/routes/settings/login/RegenerateSCIMTokenModal";
import { SCIMOperationsLog } from "@/routes/settings/login/SCIMOperationsLog";
import { useCallback, useState, type FC } from "react";

type ModalState =
    | { type: "none" }
    | { type: "configure"; config?: PlainSCIMConfiguration }
    | { type: "display-token"; config: PlainSCIMConfiguration; token: string }
    | { type: "regenerate-token"; config: PlainSCIMConfiguration };

type SCIMSectionProps = {
    organizationId: string;
    editable: boolean;
};

export const SCIMSection: FC<SCIMSectionProps> = ({ organizationId, editable }) => {
    const { data: configs = [], isLoading, error } = useListSCIMConfigurations();
    const [modalState, setModalState] = useState<ModalState>({ type: "none" });

    const handleCloseModal = useCallback(() => {
        setModalState({ type: "none" });
    }, []);

    const handleEdit = useCallback((config: PlainSCIMConfiguration) => {
        setModalState({ type: "configure", config });
    }, []);

    const handleDelete = useCallback((config: PlainSCIMConfiguration) => {
        // ConfigureSCIM modal handles delete state internally
        setModalState({ type: "configure", config });
    }, []);

    const handleRegenerateToken = useCallback((config: PlainSCIMConfiguration) => {
        setModalState({ type: "regenerate-token", config });
    }, []);

    const handleNewConfig = useCallback(() => {
        setModalState({ type: "configure" });
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <Heading3>SCIM Provisioning</Heading3>
            <Text className="text-base text-content-secondary">
                Automate user and group provisioning from your identity provider using SCIM 2.0.
            </Text>

            {isLoading && (
                <Card variant="bordered" className="bg-surface-glass p-4">
                    <Text className="text-content-tertiary">Loading SCIM configurations...</Text>
                </Card>
            )}

            {error && (
                <Card variant="bordered" className="bg-surface-glass p-4">
                    <Text className="text-content-danger">Failed to load SCIM configurations</Text>
                </Card>
            )}

            {!isLoading && !error && configs.length === 0 && (
                <Card variant="bordered" className="bg-surface-glass p-4">
                    <Text className="text-base text-content-secondary">
                        No SCIM configurations yet. Create one to enable automated user provisioning.
                    </Text>
                </Card>
            )}

            {!isLoading &&
                !error &&
                configs.map((config) => (
                    <SCIMConfigCard
                        key={config.id}
                        config={config}
                        editable={editable}
                        onEdit={() => handleEdit(config)}
                        onDelete={() => handleDelete(config)}
                        onRegenerateToken={() => handleRegenerateToken(config)}
                    />
                ))}

            <Button
                LeadingIcon={IconPlusCircle}
                disabled={!editable}
                onClick={handleNewConfig}
                data-tracking-id="new-scim-configuration"
            >
                New SCIM Configuration
            </Button>

            {/* Operations Log Section */}
            {!isLoading && !error && configs.length > 0 && (
                <>
                    <div className="mb-4 mt-8">
                        <Heading4>Operations Log</Heading4>
                        <Text className="mt-2 text-sm text-content-secondary">
                            Recent SCIM provisioning operations from your identity provider.
                        </Text>
                    </div>

                    <SCIMOperationsLog />
                </>
            )}

            {modalState.type !== "none" && (
                <Dialog open onOpenChange={(open) => !open && handleCloseModal()}>
                    <DialogContent data-track-location={TrackLocations.SCIMConfiguration}>
                        {modalState.type === "configure" && (
                            <ConfigureSCIM
                                organizationId={organizationId}
                                config={modalState.config}
                                onClose={handleCloseModal}
                            />
                        )}
                        {modalState.type === "regenerate-token" && (
                            <RegenerateSCIMTokenModal
                                config={modalState.config}
                                onClose={handleCloseModal}
                                onTokenRegenerated={(token) => {
                                    setModalState({ type: "display-token", config: modalState.config, token });
                                }}
                            />
                        )}
                        {modalState.type === "display-token" && modalState.token && (
                            <RegeneratedTokenDisplay
                                token={modalState.token}
                                onClose={handleCloseModal}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

/**
 * Component to display regenerated token (shown only once)
 */
const RegeneratedTokenDisplay: FC<{ token: string; onClose: () => void }> = ({ token, onClose }) => {
    const scimEndpoint = `${window.location.origin}/scim/v2`;

    const handleClose = useCallback(() => {
        const confirmed = window.confirm("Have you saved the token? It will not be shown again.");
        if (confirmed) {
            onClose();
        }
    }, [onClose]);

    return (
        <>
            <DialogHeader>
                <DialogTitle>Token Regenerated</DialogTitle>
                <DialogDescription>Save this token securely. It will only be shown once.</DialogDescription>
            </DialogHeader>

            <DialogBody className="flex flex-col gap-4">
                <Banner variant="warning" text="Store this token securely. It will not be shown again." />

                <div className="flex flex-col gap-2">
                    <Text className="text-sm font-medium text-content-primary">SCIM Endpoint URL</Text>
                    <Text className="break-all rounded-md border border-border-light bg-surface-pure px-3 py-2 font-mono text-sm text-content-secondary">
                        {scimEndpoint}
                    </Text>
                </div>

                <div className="flex flex-col gap-2">
                    <Text className="text-sm font-medium text-content-primary">Bearer Token</Text>
                    <Text className="break-all rounded-md border border-border-light bg-surface-pure px-3 py-2 font-mono text-sm text-content-secondary">
                        {token}
                    </Text>
                </div>

                <Banner
                    variant="info"
                    text="The previous token has been invalidated and will no longer work."
                />
            </DialogBody>

            <DialogFooter>
                <Button variant="primary" onClick={handleClose} data-tracking-id="close-regenerated-token-display">
                    Done
                </Button>
            </DialogFooter>
        </>
    );
};
